import * as React from 'react';
import ActivityEntity from '../domain/ActivityEntity';
import CardContainer from '../../../components/CardContainer';
import StepperFooter from '../../../components/StepperFooter';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { ConfigProvider, Form, message } from 'antd';
import { Typography } from 'antd';
import CategoryKeywordModel from '../models/CategoryKeywordModel';
import AntdCustomCheckbox from '../../../components/buttons/AntdCustomCheckbox';
import { BodyText } from '../../../components/ui/Typography';
import CustomTagsField from '../../../components/CustomTagsField';
import { AnimatePresence, motion } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../components/animations/StepAnimations';

const { Text } = Typography;

interface FCProps {
  stepData: {
    activityWithAreas: ActivityEntity;
  };
}

const ActivityAreasStep: React.FC<FCProps> = observer(({ stepData }) => {
  const { t } = useTranslation();
  const [isStepLoading, setIsStepLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [form] = Form.useForm();

  const nonCustomAreas = stepData.activityWithAreas.nonCustomAreas;
  const customAreas = stepData.activityWithAreas.customAreas;

  const allCheckedAreas = [...nonCustomAreas, ...customAreas].filter(
    (area) => area.checked
  );
  const disabledUncheckedAreas = allCheckedAreas.length === 3;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={staggerContainerVariants}
        >
          <CardContainer title={t('Focus')}>
            <BodyText style={{ textAlign: 'center' }}>
              <Trans
                i18nKey="areaStep.description1"
                defaults="Your business category <b>{{category}}</b> doesn't describe your business activities very precisely."
                components={{ b: <b /> }}
                values={{ category: stepData.activityWithAreas.displayName }}
              />
              <br />
              <Trans
                i18nKey="areaStep.description2"
                defaults="Please also select <b>1-3 relevant areas</b> of expertise."
                components={{ b: <b /> }}
              />
            </BodyText>
            <div className="sm:text-center">
              <div className="flex flex-col">
                <div className="mb-5">
                  <ConfigProvider
                    theme={{
                      token: {
                        fontSize: 12
                      }
                    }}
                  ></ConfigProvider>
                  <div className="flex flex-col gap-5">
                    <motion.div
                      variants={fadeInUpVariants}
                      className="flex flex-wrap gap-4 justify-start sm:justify-center"
                    >
                      {nonCustomAreas.map((area, index) => {
                        return (
                          <AntdCustomCheckbox
                            key={area.id}
                            appearance="basic"
                            checked={area.checked}
                            disabled={disabledUncheckedAreas && !area.checked}
                            onChange={(e) => {
                              stepData.activityWithAreas.setAreaChecked(
                                area.id,
                                e.target.checked
                              );
                              if (e.target.checked) {
                                stepData.activityWithAreas.removeCustomAreaByName(
                                  area.displayName.toLowerCase()
                                );
                              }
                            }}
                          >
                            <Text>{area.displayName}</Text>
                          </AntdCustomCheckbox>
                        );
                      })}
                    </motion.div>
                    <motion.div
                      variants={fadeInUpVariants}
                      className="flex sm:justify-center"
                    >
                      <CustomTagsField
                        form={form}
                        formItemName="customAreas"
                        initialValues={stepData.activityWithAreas.customAreas.map(
                          (area) => area.displayName
                        )}
                        addButtonLabel={t('Add your own focus')}
                        addFurtherButtonLabel={t('Add further focus')}
                        tagsTitle={t('Define your own focus :')}
                        maxCount={
                          disabledUncheckedAreas ? 3 - nonCustomAreas.length : 3
                        }
                        disabled={disabledUncheckedAreas}
                        placeholder={t('Press [Enter] to add your own focus.')}
                        duplicateMessage={t('This focus already exists')}
                        existingValues={nonCustomAreas
                          .filter((area) => area.checked)
                          .map((area) => area.displayName.toLowerCase())}
                        onChange={(value) => {
                          stepData.activityWithAreas.removeAllCustomAreas();
                          stepData.activityWithAreas.setCustomAreas(
                            value.map((area) => ({
                              name: area
                            }))
                          );
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
            <StepperFooter
              buttonId="category-save-next"
              buttonGaEvent="category-save-next"
              loading={isStepLoading}
              onFormsValidationSuccess={async (formData, nextStep, toStep) => {
                setIsStepLoading(true);

                const allCheckedAreas = stepData.activityWithAreas.areas.filter(
                  (area) => area.checked
                );
                if (allCheckedAreas.length < 1) {
                  message.error(
                    t('Please select at least one focus or add a custom one.')
                  );
                  setIsStepLoading(false);
                  return;
                }

                try {
                  await form.validateFields();
                } catch (error) {
                  console.error('Form validation failed:', error);
                  message.error(
                    t('Please select at least one focus or add a custom one.')
                  );
                  setIsStepLoading(false);
                  return;
                }

                try {
                  await CategoryKeywordModel.fetchKeywordsForActivitiyCustomAreas();
                  stepData.activityWithAreas.setAreasProvided(true);
                  setIsVisible(false);
                  setTimeout(async () => {
                    await CategoryKeywordModel.moveToNextStep();
                  }, 200);
                } catch (error) {
                  console.error('Error moving to next step:', error);
                  message.error(
                    t(
                      'An error occurred while fetching keywords for custom areas. Please try again.'
                    )
                  );
                  setIsStepLoading(false);
                }
              }}
              count={[...nonCustomAreas, ...customAreas].filter((area) => area.checked).length}
              maxCount={3}
              label={t('focus')}
              counterText={`${[...nonCustomAreas, ...customAreas].filter((area) => area.checked).length}/3 ${t('focus')} ${t('defined')}`}
            />
          </CardContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default ActivityAreasStep;